import React, { useCallback, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { Popover, Table } from 'antd-v5';
import { InfoCircleOutlined } from '@ant-design/icons';
import InquiryExpandedRow from './InquiryExpandedRow';
import useVendorByKey from '../../../../hooks/query/useVendorByKey';
import { isTerminalStatus, columnSortOrder } from '../utils';
import formatNumberAsCurrency from '../../../../utils/formatNumberAsCurrency';

const TOTAL_SPEND_INFO = (
  <div style={{ maxWidth: 320 }}>
    <strong>Total spend</strong> is this client&apos;s lifetime spend across all available purchases from your Point of
    Sale. It refreshes when you open the inquiry&apos;s detail row or the client&apos;s Rolex tab. See the client
    profile for a full purchase breakdown.
  </div>
);

const InquiryTable = ({
  data,
  loading,
  onEdit,
  canEdit,
  page,
  pageSize,
  total,
  onPageChange,
  onSortChange,
  sortBy,
  sortOrder,
  hasPos,
}) => {
  const history = useHistory();
  const { vendorId: rolexVendorId } = useVendorByKey('rolex');
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);

  const handleExpandRow = useCallback((expanded, record) => {
    if (expanded) {
      setExpandedRowKeys(prev => [...prev, record.id]);
    } else {
      setExpandedRowKeys(prev => prev.filter(key => key !== record.id));
    }
  }, []);

  // Route antd Table changes: sort -> parent (server-side refetch), page -> onPageChange.
  const handleTableChange = (pagination, _filters, sorter, extra) => {
    if (extra.action === 'sort') {
      const s = Array.isArray(sorter) ? sorter[0] : sorter;
      onSortChange(s?.columnKey, s?.order);
    } else if (extra.action === 'paginate') {
      onPageChange(pagination.current, pagination.pageSize);
    }
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'clientName',
      key: 'clientName',
      sorter: true,
      sortOrder: columnSortOrder('clientName', sortBy, sortOrder),
      render: (text, record) => (
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions
        <span
          className="inquiry-cell-name"
          data-testid="inquiries__table--name"
          onClick={() => history.push(`/Clients?client=${record.clientId}&clientTab=rolex`)}
        >
          {text}
        </span>
      ),
    },
    {
      title: 'Inquiry Date',
      dataIndex: 'inquiryDate',
      key: 'inquiryDate',
      sorter: true,
      sortOrder: columnSortOrder('inquiryDate', sortBy, sortOrder),
      render: (text, record) => (
        <div className="inquiry-cell-date">
          <span className="inquiry-cell-date__value">{text}</span>
          <span className="inquiry-cell-date__duration">{record.durationMonths}</span>
        </div>
      ),
    },
    {
      title: 'Product',
      dataIndex: 'products',
      key: 'products',
      render: products => (
        <div className="inquiry-cell-product">
          {(products || []).map((product, idx) => {
            const canNavigate = !!(rolexVendorId && product.vendorItemId);
            return (
              <div className="inquiry-cell-product__line" key={product.vendorItemId || `${product.mpn}-${idx}`}>
                {canNavigate ? (
                  <Link
                    to={`/Products?vendorId=${rolexVendorId}&productId=${product.vendorItemId}`}
                    className="inquiry-cell-product__name"
                  >
                    {product.name}
                  </Link>
                ) : (
                  <span className="inquiry-cell-product__name-text">{product.name}</span>
                )}
                <span className="inquiry-cell-product__mpn">{product.mpn}</span>
              </div>
            );
          })}
        </div>
      ),
    },
    {
      title: (
        <span>
          Total Spend{' '}
          <Popover content={TOTAL_SPEND_INFO} trigger={['click', 'hover']}>
            <InfoCircleOutlined
              style={{ opacity: 0.45, marginLeft: 2, cursor: 'pointer' }}
              onClick={e => e.stopPropagation()}
            />
          </Popover>
        </span>
      ),
      dataIndex: 'totalSpend',
      key: 'totalSpend',
      sorter: true,
      sortOrder: columnSortOrder('totalSpend', sortBy, sortOrder),
      render: value => (value == null ? '' : formatNumberAsCurrency(value)),
      hidden: !hasPos,
    },
    {
      title: 'Last Inbound',
      dataIndex: 'lastInbound',
      key: 'lastInbound',
      sorter: true,
      sortOrder: columnSortOrder('lastInbound', sortBy, sortOrder),
      render: (text, record) => (
        <div className="inquiry-cell-date">
          <span className="inquiry-cell-date__value">{text}</span>
          <span className="inquiry-cell-date__duration">{record.lastInboundDuration}</span>
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: value => {
        const label = { WAITING: 'Waiting', ORDERED: 'Ordered', CANCELED: 'Canceled', FULFILLED: 'Fulfilled' };
        return label[value] || value;
      },
    },
    {
      title: '',
      key: 'edit',
      width: 60,
      render: (_, record) => {
        if (canEdit && !canEdit(record)) return null;
        return (
          // eslint-disable-next-line jsx-a11y/no-static-element-interactions
          <span
            className="inquiry-cell-edit"
            data-testid="inquiries__table--edit-button"
            onClick={() => onEdit(record)}
          >
            {isTerminalStatus(record.status) ? 'View' : 'Edit'}
          </span>
        );
      },
    },
  ];

  return (
    <div className="rolex-inquiries__table">
      <Table
        columns={columns}
        dataSource={data}
        rowKey="id"
        loading={loading}
        onRow={() => ({ 'data-testid': 'inquiries__table--row' })}
        expandable={{
          // eslint-disable-next-line react/no-unstable-nested-components
          expandedRowRender: record => <InquiryExpandedRow inquiry={record} />,
          expandedRowKeys,
          onExpand: handleExpandRow,
        }}
        onChange={handleTableChange}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: { showSearch: false },
          showTotal: t => `Total ${t} items`,
        }}
        size="middle"
      />
    </div>
  );
};

export default InquiryTable;
